import React, { useState, useEffect, useContext } from "react";
import AdminMasterLayout from "../../../layout/superadmin/MasterLayout";
import DashboardStatCards from "../../../subscreens/superadmin/dashboard/dashboardstatcard/DashboardStatcard";
import DashboardChart from "../../../subscreens/superadmin/dashboard/dashboardchart/DashboardChart";
import CriticalAlerts from "../../../subscreens/superadmin/dashboard/criticalalert/CriticalAlert";
import CommissionRateManager from "../../../subscreens/superadmin/dashboard/commitionrate/Commitionrate";
import Summary from "../../../subscreens/superadmin/dashboard/summary/Summary";
import '../../../assets/css/superadmin/dashboard/Dashboard.css';
import AppContext from "../../../context/AppContext";
import axios from "axios";

/* ══════════════════════════════════════════
   SKELETON COMPONENTS
══════════════════════════════════════════ */

const SkeletonStatCards = () => (
  <div className="dashboard-stats-grid">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="dash-skel-stat-card">
        <div className="dash-skel-stat-inner">
          <div className="dash-skel-stat-text">
            <div className="dash-skel dash-skel-stat-title" />
            <div className="dash-skel dash-skel-stat-value" />
            <div className="dash-skel dash-skel-stat-change" />
          </div>
          <div className="dash-skel dash-skel-stat-icon" />
        </div>
      </div>
    ))}
  </div>
);

const SkeletonSectionHeader = () => (
  <div className="ad-section-header">
    <div className="dash-skel dash-skel-subtitle" />
    <div className="dash-skel dash-skel-commission-btn" />
  </div>
);

const SkeletonChart = () => (
  <div className="dash-skel-chart-card">
    <div className="dash-skel-chart-header">
      <div className="dash-skel-chart-title-section">
        <div className="dash-skel dash-skel-chart-title" />
        <div className="dash-skel dash-skel-chart-subtitle" />
      </div>
      <div className="dash-skel-chart-controls">
        <div className="dash-skel dash-skel-chart-btn" />
        <div className="dash-skel dash-skel-chart-btn" />
      </div>
    </div>
    <div className="dash-skel dash-skel-chart-body" />
  </div>
);

const SkeletonCriticalAlert = () => (
  <div className="dash-skel-alert-card">
    <div className="dash-skel-alert-header">
      <div className="dash-skel dash-skel-alert-title" />
      <div className="dash-skel dash-skel-alert-subtitle" />
    </div>
    <div className="dash-skel dash-skel-alert-chart" />
    <div className="dash-skel-alert-summary">
      <div className="dash-skel dash-skel-alert-sum-label" />
      <div className="dash-skel dash-skel-alert-sum-value" />
    </div>
  </div>
);

const SkeletonSummaryRow = ({ cols = 3 }) => (
  <div className="dash-skel-summary-card">
    <div className="dash-skel-summary-header">
      <div className="dash-skel dash-skel-summary-title" />
      <div className="dash-skel dash-skel-summary-btn" />
    </div>
    <div className="dash-skel-summary-table-head">
      {Array(cols).fill(0).map((_, i) => (
        <div key={i} className="dash-skel dash-skel-th" />
      ))}
    </div>
    {[1, 2].map((i) => (
      <div key={i} className="dash-skel-summary-row">
        <div className="dash-skel-summary-cell-avatar">
          <div className="dash-skel dash-skel-avatar" />
          <div className="dash-skel-summary-cell-text">
            <div className="dash-skel dash-skel-cell-name" />
            <div className="dash-skel dash-skel-cell-sub" />
          </div>
        </div>
        <div className="dash-skel dash-skel-cell-mid" />
        <div className="dash-skel dash-skel-cell-badge" />
      </div>
    ))}
  </div>
);

const SkeletonSummaryBlog = () => (
  <div className="dash-skel-summary-card">
    <div className="dash-skel-summary-header">
      <div className="dash-skel dash-skel-summary-title" />
      <div className="dash-skel dash-skel-summary-btn" />
    </div>
    {[1, 2].map((i) => (
      <div key={i} className="dash-skel-blog-row">
        <div className="dash-skel dash-skel-blog-thumb" />
        <div className="dash-skel-blog-info">
          <div className="dash-skel dash-skel-blog-cat" />
          <div className="dash-skel dash-skel-blog-title" />
        </div>
        <div className="dash-skel dash-skel-blog-arrow" />
      </div>
    ))}
  </div>
);

/* ══════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
══════════════════════════════════════════ */

const Dashboard = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [isDark, setIsDark] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const getDashboardData = () => {
    axios.get(`${baseUrl}/admin/dashboard`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((response) => {
        if (response.data.success) {
          setDashboardData(response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  return (
    <AdminMasterLayout>
      <div className={`ad-shell ${isDark ? "dark-mode" : "light-mode"}`}>
        <main className="ad-main">

          {isLoading ? (
            <>
              <section className="ad-card-shell">
                <SkeletonSectionHeader />
                <SkeletonStatCards />
              </section>

              <section className="ad-card-shell">
                <div className="ad-grid-layout">
                  <SkeletonChart />
                  <SkeletonCriticalAlert />
                </div>
              </section>

              <section className="ad-card-shell">
                <div className="dov-wrapper" style={{ padding: 0 }}>
                  <div className="dov-container">
                    <div className="dov-row row g-4">
                      <div className="col-lg-6"><SkeletonSummaryRow cols={3} /></div>
                      <div className="col-lg-6"><SkeletonSummaryRow cols={3} /></div>
                      <div className="col-lg-6"><SkeletonSummaryRow cols={3} /></div>
                      <div className="col-lg-6"><SkeletonSummaryBlog /></div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <>
              <section className="ad-card-shell">
                <div className="ad-section-header">
                  <p className="ad-section-subtitle">Welcome back, Administrator</p>
                  <CommissionRateManager />
                </div>
                <DashboardStatCards stats={dashboardData?.stats} />
              </section>

              <section className="ad-card-shell">
                <div className="ad-grid-layout">
                  <DashboardChart chartData={dashboardData?.chart} />
                  <CriticalAlerts maintenanceCost={dashboardData?.maintenanceCost} />
                </div>
              </section>

              <section className="ad-card-shell">
                <Summary
                  disputes={dashboardData?.recentDisputes}
                  payouts={dashboardData?.recentPayouts}
                  bookings={dashboardData?.recentBookings}
                  blogs={dashboardData?.latestBlogs}
                />
              </section>
            </>
          )}

        </main>
      </div>
    </AdminMasterLayout>
  );
};

export default Dashboard;